import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Star, MessageSquareText, RefreshCw, ThumbsUp, ThumbsDown, Lightbulb } from "lucide-react";
import { fetchFeedbacks } from "../../services/feedbackService";

function fmtDate(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function StarsDisplay({ rating, darkMode }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} su 5 stelle`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-4 h-4 ${
            n <= rating
              ? "fill-forfy-500 text-forfy-500"
              : darkMode
                ? "fill-transparent text-neutral-600"
                : "fill-transparent text-neutral-300"
          }`}
        />
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, darkMode, accent }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent || (darkMode ? "text-neutral-400" : "text-neutral-500")}`} />
        <span className={`text-xs font-medium ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
          {label}
        </span>
      </div>
      <div className={`text-2xl font-semibold ${darkMode ? "text-neutral-100" : "text-neutral-900"}`}>
        {value}
      </div>
    </div>
  );
}

/**
 * Card in evidenza con la media rating complessiva.
 */
function AverageRatingCard({ average, total, darkMode }) {
  return (
    <div
      className={`rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 ${
        darkMode
          ? "bg-gradient-to-br from-forfy-900/20 to-neutral-900 border-forfy-800/40"
          : "bg-gradient-to-br from-forfy-50 to-white border-forfy-200"
      }`}
    >
      <div>
        <div className={`text-xs font-medium uppercase tracking-wide ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
          Media rating complessiva
        </div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className={`text-4xl font-bold font-display ${darkMode ? "text-forfy-300" : "text-forfy-600"}`}>
            {total ? average.toFixed(1) : "—"}
          </span>
          <span className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>/ 5</span>
        </div>
      </div>
      {total > 0 && (
        <div className="flex items-center gap-1">
          <StarsDisplay rating={Math.round(average)} darkMode={darkMode} />
        </div>
      )}
      <div className={`sm:ml-auto text-sm ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
        basata su {total} feedback{total === 1 ? "" : ""}
      </div>
    </div>
  );
}

export default function FeedbackAdminSection({ darkMode }) {
  const [feedbacks, setFeedbacks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ratingFilter, setRatingFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFeedbacks();
      setFeedbacks(data);
    } catch (err) {
      console.error("FeedbackAdminSection fetch error:", err);
      setError(err?.message || "Errore nel caricamento dei feedback.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const list = feedbacks || [];
    if (ratingFilter === "all") return list;
    const n = Number(ratingFilter);
    return list.filter((f) => f.rating === n);
  }, [feedbacks, ratingFilter]);

  const stats = useMemo(() => {
    const list = feedbacks || [];
    const total = list.length;
    const sum = list.reduce((acc, f) => acc + (f.rating || 0), 0);
    const average = total ? sum / total : 0;
    const withLikes = list.filter((f) => f.likes && f.likes.trim()).length;
    const withDislikes = list.filter((f) => f.dislikes && f.dislikes.trim()).length;
    const withSuggestions = list.filter((f) => f.suggestions && f.suggestions.trim()).length;
    return { total, average, withLikes, withDislikes, withSuggestions };
  }, [feedbacks]);

  const showSkeleton = loading && !feedbacks;
  const showEmpty = !loading && feedbacks && filtered.length === 0;

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className={`font-display text-lg font-semibold ${darkMode ? "text-neutral-100" : "text-neutral-900"}`}>
          Feedback utenti
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            loading
              ? darkMode
                ? "bg-neutral-800 text-neutral-500"
                : "bg-neutral-200 text-neutral-400"
              : darkMode
                ? "bg-forfy-500/15 text-forfy-300 hover:bg-forfy-500/25"
                : "bg-forfy-50 text-forfy-700 hover:bg-forfy-100"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {error && (
        <div className={`mb-3 p-3 rounded-lg text-sm ${darkMode ? "bg-red-900/30 text-red-200" : "bg-red-50 text-red-700"}`}>
          {error}
        </div>
      )}

      {/* Media rating in evidenza */}
      <div className="mb-3">
        <AverageRatingCard average={stats.average} total={stats.total} darkMode={darkMode} />
      </div>

      {/* Statistiche aggregate */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard icon={MessageSquareText} label="Totale feedback" value={stats.total} darkMode={darkMode} />
        <StatCard
          icon={ThumbsUp}
          label="Con 'mi piace'"
          value={stats.withLikes}
          darkMode={darkMode}
          accent={darkMode ? "text-green-400" : "text-green-600"}
        />
        <StatCard
          icon={ThumbsDown}
          label="Con criticità"
          value={stats.withDislikes}
          darkMode={darkMode}
          accent={darkMode ? "text-red-400" : "text-red-500"}
        />
        <StatCard
          icon={Lightbulb}
          label="Con suggerimenti"
          value={stats.withSuggestions}
          darkMode={darkMode}
          accent={darkMode ? "text-amber-400" : "text-amber-500"}
        />
      </div>

      {/* Filtro rating */}
      <div
        className={`flex flex-col sm:flex-row gap-2 sm:items-center mb-3 p-3 rounded-xl border ${
          darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
        }`}
      >
        <label className={`text-xs font-medium ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
          Rating
        </label>
        <select
          value={ratingFilter}
          onChange={(e) => setRatingFilter(e.target.value)}
          className={`px-2.5 py-1.5 rounded-lg text-sm border outline-none ${
            darkMode ? "bg-neutral-800 border-neutral-700 text-neutral-200" : "bg-neutral-50 border-neutral-200 text-neutral-700"
          }`}
        >
          <option value="all">Tutti</option>
          <option value="5">5 stelle</option>
          <option value="4">4 stelle</option>
          <option value="3">3 stelle</option>
          <option value="2">2 stelle</option>
          <option value="1">1 stella</option>
        </select>

        <span className={`sm:ml-auto text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
          {filtered.length} risultat{filtered.length === 1 ? "o" : "i"}
        </span>
      </div>

      {/* Lista feedback */}
      <div className={`rounded-xl border overflow-hidden ${darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"}`}>
        {showSkeleton && (
          <div className="p-4 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`h-16 rounded animate-pulse ${darkMode ? "bg-neutral-800" : "bg-neutral-200"}`} />
            ))}
          </div>
        )}

        {showEmpty && (
          <div className={`p-10 text-center text-sm ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
            Nessun feedback trovato
          </div>
        )}

        {!showSkeleton && filtered.length > 0 && (
          <div className={`divide-y ${darkMode ? "divide-neutral-800" : "divide-neutral-100"}`}>
            {filtered.map((f) => (
              <div key={f.id} className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <StarsDisplay rating={f.rating} darkMode={darkMode} />
                  <div className={`flex items-center gap-3 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
                    <span className="font-mono break-all">{f.userEmail || "—"}</span>
                    <span>{fmtDate(f.createdAtMs)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                  {f.likes && (
                    <div>
                      <div className={`text-xs font-semibold mb-1 flex items-center gap-1 ${darkMode ? "text-green-400" : "text-green-600"}`}>
                        <ThumbsUp className="w-3 h-3" /> Cosa piace
                      </div>
                      <p className={`text-sm whitespace-pre-wrap ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                        {f.likes}
                      </p>
                    </div>
                  )}
                  {f.dislikes && (
                    <div>
                      <div className={`text-xs font-semibold mb-1 flex items-center gap-1 ${darkMode ? "text-red-400" : "text-red-500"}`}>
                        <ThumbsDown className="w-3 h-3" /> Cosa non piace
                      </div>
                      <p className={`text-sm whitespace-pre-wrap ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                        {f.dislikes}
                      </p>
                    </div>
                  )}
                  {f.suggestions && (
                    <div>
                      <div className={`text-xs font-semibold mb-1 flex items-center gap-1 ${darkMode ? "text-amber-400" : "text-amber-500"}`}>
                        <Lightbulb className="w-3 h-3" /> Suggerimenti
                      </div>
                      <p className={`text-sm whitespace-pre-wrap ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                        {f.suggestions}
                      </p>
                    </div>
                  )}
                  {!f.likes && !f.dislikes && !f.suggestions && (
                    <p className={`text-sm italic ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
                      Nessun commento aggiuntivo.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
